/**
 * Search modal off:
 * 1) Predictive search in the header row — icon opens layer on the current page; no navigation until Enter (or modified click opens /search in a new tab).
 * 2) Inline search bar styling is fixed in header-expand-search.css (not matched to the site header).
 */
(function () {
  const STORAGE_KEY = "kentHeaderExpandSearchFocus";
  let inlineSearchCloseGen = 0;

  function isDesktopHeaderLayout() {
    return window.matchMedia && window.matchMedia("(min-width: 1200px)").matches;
  }

  /** True when the header search chrome should visually extend upward (cover site switcher / rows above #section-header). */
  function needsHeaderSearchBleed() {
    const h = document.getElementById("section-header");
    if (h && h.classList.contains("header--inline-search-open")) return true;
    if (
      h &&
      h.classList.contains("header--page-search-active") &&
      !h.classList.contains("header--page-search-collapsed")
    ) {
      return true;
    }
    const mh = document.getElementById("header-mobile-menu");
    if (mh && mh.classList.contains("header-mobile--inline-search-open")) return true;
    return false;
  }

  function syncHeaderSearchBleed() {
    const root = document.documentElement;
    if (!root.classList.contains("header-predictive-inline-enabled")) {
      root.style.removeProperty("--header-inline-search-cover-height");
      return;
    }
    if (!needsHeaderSearchBleed()) {
      root.style.removeProperty("--header-inline-search-cover-height");
      return;
    }
    let topPx = 0;
    if (isDesktopHeaderLayout()) {
      const h = document.getElementById("section-header");
      if (h) topPx = Math.max(0, Math.round(h.getBoundingClientRect().top));
    } else {
      const m = document.getElementById("header-mobile-menu");
      if (m) topPx = Math.max(0, Math.round(m.getBoundingClientRect().top));
    }
    root.style.setProperty("--header-inline-search-cover-height", topPx + "px");
  }

  function initHeaderInlineSearchLayout() {
    syncHeaderSearchBleed();
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        syncHeaderSearchBleed();
      });
    });

    let timeoutId = null;
    function debouncedBleedSync() {
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(function () {
        timeoutId = null;
        syncHeaderSearchBleed();
      }, 100);
    }

    window.addEventListener("resize", debouncedBleedSync, { passive: true });
    window.addEventListener("scroll", debouncedBleedSync, { passive: true });
    window.addEventListener("load", function () {
      syncHeaderSearchBleed();
    }, { passive: true });

    const desktopHeader = document.getElementById("section-header");
    const mobileHeader = document.getElementById("header-mobile-menu");
    if (typeof ResizeObserver !== "undefined") {
      if (desktopHeader) {
        new ResizeObserver(debouncedBleedSync).observe(desktopHeader);
        const group =
          desktopHeader.closest("[id^='shopify-section-group-']") ||
          desktopHeader.closest(".shopify-section-group");
        if (group) {
          new ResizeObserver(debouncedBleedSync).observe(group);
        }
      }
      if (mobileHeader) {
        new ResizeObserver(debouncedBleedSync).observe(mobileHeader);
      }
    }
  }

  if (document.documentElement.classList.contains("header-predictive-inline-enabled")) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initHeaderInlineSearchLayout);
    } else {
      initHeaderInlineSearchLayout();
    }
  }

  function getHeader() {
    return document.getElementById("section-header");
  }

  function getDeskPanel() {
    return document.getElementById("header-inline-search-panel");
  }

  function getMobPanel() {
    return document.getElementById("header-inline-search-panel-mobile");
  }

  function getMobileHeader() {
    return document.getElementById("header-mobile-menu");
  }

  /** Mobile: /search?q=… is server-rendered with panel open (no sessionStorage). */
  function isMobileInlineSearchOpenFromDocument() {
    const mHeader = getMobileHeader();
    const mob = getMobPanel();
    return Boolean(
      mHeader &&
        mHeader.classList.contains("header-mobile--inline-search-open") &&
        mob &&
        !mob.hidden,
    );
  }

  function clearInlineSearchLandedNoTransitionClass() {
    const root = document.documentElement;
    if (!root.classList.contains("header-inline-search-landed-with-query")) return;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        root.classList.remove("header-inline-search-landed-with-query");
      });
    });
  }

  function getToggles() {
    return document.querySelectorAll(".header-inline-search-toggle");
  }

  function ensureSearchBackdrop() {
    let el = document.getElementById("header-inline-search-backdrop");
    if (!el) {
      el = document.createElement("div");
      el.id = "header-inline-search-backdrop";
      el.className = "header-inline-search-backdrop";
      el.setAttribute("aria-hidden", "true");
      document.body.appendChild(el);
    }
    return el;
  }

  function setSearchDim(active) {
    const root = document.documentElement;
    if (!root.classList.contains("header-predictive-inline-enabled")) {
      root.classList.remove("header-inline-search-dim");
      return;
    }
    if (active) {
      ensureSearchBackdrop();
      root.classList.add("header-inline-search-dim");
    } else {
      root.classList.remove("header-inline-search-dim");
    }
  }

  function isAnyPanelOpen() {
    const desk = getDeskPanel();
    const mob = getMobPanel();
    const header = getHeader();
    const mHeader = getMobileHeader();
    if (desk && !desk.hidden) return true;
    if (mob && !mob.hidden) return true;
    if (header && header.classList.contains("header--inline-search-open")) return true;
    if (mHeader && mHeader.classList.contains("header-mobile--inline-search-open")) return true;
    return false;
  }

  /** True when the search field layer is expanded (not mid-close). Used for toggle icon only. */
  function isInlineSearchPanelExpanded() {
    const desk = getDeskPanel();
    const mob = getMobPanel();
    return Boolean((desk && !desk.hidden) || (mob && !mob.hidden));
  }

  function setTogglesExpanded(expanded) {
    getToggles().forEach(function (el) {
      el.setAttribute("aria-expanded", expanded ? "true" : "false");
    });
  }

  function toggleAllowsDefaultNavigation(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return true;
    if ("button" in event && event.button != null && event.button !== 0) return true;
    return false;
  }

  /** After full-page navigation from header search submit, reopen chrome and focus the right field (mobile vs desktop). */
  function restoreSearchChromeAfterNavigationFromStorage() {
    try {
      if (!sessionStorage.getItem(STORAGE_KEY)) return;
      sessionStorage.removeItem(STORAGE_KEY);
      if (!document.documentElement.classList.contains("header-predictive-inline-enabled")) return;

      if (isDesktopHeaderLayout()) {
        const templateInput = document.getElementById("Search-In-Template");
        if (templateInput) {
          window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
              templateInput.focus({ preventScroll: true });
              if (typeof templateInput.select === "function") templateInput.select();
              syncHeaderSearchBleed();
            });
          });
        }
        return;
      }

      const mob = getMobPanel();
      const mHeader = getMobileHeader();
      if (!mob || !mHeader) return;

      const desk = getDeskPanel();
      const header = getHeader();
      if (desk) desk.hidden = true;
      if (header) header.classList.remove("header--inline-search-open");
      const psD = desk && desk.querySelector("predictive-search");
      if (psD && typeof psD.closeResults === "function") psD.closeResults(true);

      mHeader.classList.add("header-mobile--inline-search-open");
      mob.hidden = false;
      setTogglesExpanded(true);
      setSearchDim(true);

      const input = document.getElementById("Search-In-Header-mobile");
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          syncHeaderSearchBleed();
          if (input) {
            input.focus({ preventScroll: true });
            if (typeof input.select === "function") input.select();
          }
        });
      });
    } catch (e) {
      /* ignore */
    }
  }

  function openPanel() {
    inlineSearchCloseGen += 1;
    const desk = getDeskPanel();
    const mob = getMobPanel();
    const header = getHeader();
    const mHeader = getMobileHeader();
    const desktop = isDesktopHeaderLayout();

    if (desktop) {
      if (mob) {
        mob.hidden = true;
        if (mHeader) mHeader.classList.remove("header-mobile--inline-search-open");
        const psM = mob.querySelector("predictive-search");
        if (psM && typeof psM.closeResults === "function") psM.closeResults(true);
      }
      if (header && desk) {
        header.classList.add("header--inline-search-open");
        desk.hidden = false;
      }
    } else {
      if (desk) {
        desk.hidden = true;
        if (header) header.classList.remove("header--inline-search-open");
        const psD = desk.querySelector("predictive-search");
        if (psD && typeof psD.closeResults === "function") psD.closeResults(true);
      }
      if (mHeader && mob) {
        mHeader.classList.add("header-mobile--inline-search-open");
        mob.hidden = false;
      }
    }

    setTogglesExpanded(true);
    setSearchDim(true);

    const input = desktop
      ? document.getElementById("Search-In-Header")
      : document.getElementById("Search-In-Header-mobile");

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        syncHeaderSearchBleed();
        if (input) {
          input.focus({ preventScroll: true });
        }
      });
    });
  }

  function closePanel() {
    const header = getHeader();
    const mHeader = getMobileHeader();
    const desk = getDeskPanel();
    const mob = getMobPanel();

    function finishClose() {
      if (header) header.classList.remove("header--inline-search-open");
      if (mHeader) mHeader.classList.remove("header-mobile--inline-search-open");
      ;[desk, mob].forEach(function (panel) {
        if (!panel) return;
        panel.hidden = true;
        const ps = panel.querySelector("predictive-search");
        if (ps && typeof ps.closeResults === "function") {
          ps.closeResults(true);
        }
      });
      setTogglesExpanded(false);
      setSearchDim(false);
      syncHeaderSearchBleed();
    }

    const activePanel =
      desk && !desk.hidden ? desk : mob && !mob.hidden ? mob : null;

    if (!activePanel) {
      inlineSearchCloseGen += 1;
      finishClose();
      return;
    }

    const op = (inlineSearchCloseGen += 1);

    setSearchDim(false);
    setTogglesExpanded(false);

    let done = false;
    function complete() {
      if (op !== inlineSearchCloseGen) return;
      if (done) return;
      done = true;
      finishClose();
    }

    const onEnd = function (e) {
      if (op !== inlineSearchCloseGen) return;
      if (e.target !== activePanel) return;
      if (e.propertyName !== "opacity") return;
      activePanel.removeEventListener("transitionend", onEnd);
      complete();
    };
    activePanel.addEventListener("transitionend", onEnd);
    activePanel.hidden = true;

    window.setTimeout(function () {
      if (op !== inlineSearchCloseGen) return;
      activePanel.removeEventListener("transitionend", onEnd);
      complete();
    }, 550);

    ;[desk, mob].forEach(function (panel) {
      if (!panel || panel === activePanel) return;
      panel.hidden = true;
      const ps = panel.querySelector("predictive-search");
      if (ps && typeof ps.closeResults === "function") {
        ps.closeResults(true);
      }
    });
  }

  function togglePanel() {
    if (isInlineSearchPanelExpanded()) closePanel();
    else openPanel();
  }

  document.addEventListener(
    "click",
    function (event) {
      if (!document.documentElement.classList.contains("header-predictive-inline-enabled")) {
        return;
      }

      const toggle = event.target.closest(".header-inline-search-toggle");
      if (toggle) {
        if (!toggleAllowsDefaultNavigation(event)) {
          event.preventDefault();
          togglePanel();
        }
        return;
      }

      if (!isAnyPanelOpen()) return;

      const desk = getDeskPanel();
      const mob = getMobPanel();
      if ((desk && desk.contains(event.target)) || (mob && mob.contains(event.target))) {
        return;
      }
      closePanel();
    },
    true,
  );

  document.addEventListener(
    "keydown",
    function (event) {
      if (event.code !== "Enter" && event.code !== " ") return;
      if (!document.documentElement.classList.contains("header-predictive-inline-enabled")) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const toggle = event.target.closest(".header-inline-search-toggle");
      if (!toggle) return;
      event.preventDefault();
      togglePanel();
    },
    true,
  );

  document.addEventListener("keydown", function (event) {
    if (event.code !== "Escape") return;
    const header = getHeader();
    if (
      header &&
      header.classList.contains("header--page-search-active") &&
      !header.classList.contains("header--page-search-collapsed")
    ) {
      header.classList.add("header--page-search-collapsed");
      setSearchDim(false);
      const layer = header.querySelector(".header__page-search-layer");
      if (layer) {
        const ps = layer.querySelector("predictive-search");
        if (ps && typeof ps.closeResults === "function") ps.closeResults(true);
      }
      syncHeaderSearchBleed();
      return;
    }
    if (!isAnyPanelOpen()) return;
    closePanel();
  });

  document.addEventListener("submit", function (event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.id !== "header-inline-search-form" && form.id !== "header-inline-search-form-mobile") return;
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {
      /* ignore */
    }
  });

  document.addEventListener("click", function (event) {
    if (!event.target.closest(".header-inline-search-reset")) return;
    const desk = getDeskPanel();
    const mob = getMobPanel();
    const inDesk = desk && desk.contains(event.target);
    const inMob = mob && mob.contains(event.target);
    if (!inDesk && !inMob) return;
    window.setTimeout(function () {
      const input = inDesk
        ? document.getElementById("Search-In-Header")
        : document.getElementById("Search-In-Header-mobile");
      if (!input || input.value.length === 0) {
        closePanel();
      }
    }, 0);
  });

  /**
   * Search template: main section clear → bare /search.
   * Search page header overlay clear → dismiss overlay (stay on page); full reload would re-apply server overlay class.
   */
  document.addEventListener(
    "click",
    function (event) {
      const btn = event.target.closest(".search-template-reset-default");
      if (!btn) return;

      const header = document.getElementById("section-header");
      const inPageSearchLayer = Boolean(btn.closest(".header__page-search-layer"));
      if (
        header &&
        header.classList.contains("header--page-search-active") &&
        inPageSearchLayer &&
        header.contains(btn)
      ) {
        event.preventDefault();
        const ps = btn.closest("predictive-search");
        if (ps && typeof ps.closeResults === "function") {
          ps.closeResults(true);
        }
        const root = document.documentElement;
        if (root.classList.contains("header-predictive-inline-enabled")) {
          root.classList.remove("header-inline-search-dim");
        }
        header.classList.add("header--page-search-collapsed");
        syncHeaderSearchBleed();
        return;
      }

      event.preventDefault();
      const ps2 = btn.closest("predictive-search");
      if (ps2 && typeof ps2.closeResults === "function") {
        ps2.closeResults(true);
      }
      const root2 = document.documentElement;
      if (root2.classList.contains("header-predictive-inline-enabled")) {
        root2.classList.remove("header-inline-search-dim");
      }
      const url = window.routes && window.routes.search_url ? window.routes.search_url : "/search";
      window.location.assign(url);
    },
    true,
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      if (isMobileInlineSearchOpenFromDocument()) {
        setTogglesExpanded(true);
        syncHeaderSearchBleed();
        clearInlineSearchLandedNoTransitionClass();
      } else {
        setTogglesExpanded(false);
      }
      restoreSearchChromeAfterNavigationFromStorage();
    });
  } else {
    if (isMobileInlineSearchOpenFromDocument()) {
      setTogglesExpanded(true);
      syncHeaderSearchBleed();
      clearInlineSearchLandedNoTransitionClass();
    } else {
      setTogglesExpanded(false);
    }
    restoreSearchChromeAfterNavigationFromStorage();
  }
})();
